// components/settings/Profile.js
import React, { useState, useEffect } from "react";
// Removed redux useSelector in favour of zustand store
import { API_BASE } from "../../Utils/constant";
import { toast } from "react-toastify";
import NameDisplay from "../NameDisplay";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../Utils/firebase";
import { AUTH_TOKEN } from "../../constants/localStorageKeys";
import useUserStore from "../../zustand-stores/UserStore";
import { retrieve, save } from "../../helpers/StorageHelper";
import { USER_DATA } from "../../constants/localStorageKeys";
import { useQueryClient } from "@tanstack/react-query";

const Profile = () => {
  const { user, setUser } = useUserStore();
  const queryClient = useQueryClient();

  // Sync local state with user from store
  useEffect(() => {
    if (user) {
      setImage(user?.image || user?.profileImage || null);
      setFormData({
        full_name: user?.full_name || "",
        email: user?.email || "",
        phone_number: user?.phone_number || "",
        occupation: user?.occupation || "",
        image: user?.image || user?.profileImage || null,
      });
    }
  }, [user]);

  const [isEditing, setIsEditing] = useState(false);
  const [image, setImage] = useState(user?.image || null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleEditClick = () => setIsEditing(true);

  const updateUser = async (userId, updatedData) => {
    try {
      const userRef = doc(db, "users", String(userId));
      await updateDoc(userRef, updatedData);

    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleCancelClick = (e) => {
    e.preventDefault();
    setIsEditing(false);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setError("");
    setSuccessMessage("");

    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("image", file);
      const token = retrieve(AUTH_TOKEN);

      const response = await fetch(`${API_BASE}/user/update/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (response.ok && data.status === true) {
        toast.success("Image uploaded successfully!");
        const imageUrl = data.image || previewUrl;

        // Update Firebase
        if (user?.id) {
          await updateUser(String(user.id), { profileImage: imageUrl });
        }

        // Update zustand store and localStorage
        const updatedUser = {
          ...user,
          image: imageUrl,
          profileImage: imageUrl,
        };
        setUser(updatedUser);

        // Update local state
        setImage(imageUrl);
        setImagePreview(null); // Clear preview after successful upload

        // Invalidate React Query cache to refresh HeaderLeftUser and other components
        queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      } else {
        setError("Upload failed.");
        toast.error("Upload failed.");
      }
    } catch (err) {
      setError("Upload error.");
      toast.error("Upload error.");
    } finally {
      setIsUploading(false);
    }
  };

  const [formData, setFormData] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone_number: user?.phone_number || "",
    occupation: user?.occupation || "",
    image: user?.image || null,
  });

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    if (!/^[0-9]{10}$/.test(formData.phone_number)) {
      setError("Phone number must be 10 digits.");
      return;
    }
    const updatedFormData = { ...formData, image: null };
    const token = retrieve(AUTH_TOKEN);
    try {
      setIsUpdating(true);
      const response = await fetch(`${API_BASE}/user/update/profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedFormData),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success("Profile updated successfully!");

        // Update Firebase
        if (user?.id) {
          await updateUser(String(user.id), {
            name: formData.full_name,
            full_name: formData.full_name,
            email: formData.email,
            phoneNumber: formData.phone_number,
            phone_number: formData.phone_number,
            occupation: formData.occupation,
          });
        }

        // Update zustand store and localStorage
        const updatedUser = {
          ...user,
          full_name: formData.full_name,
          email: formData.email,
          phone_number: formData.phone_number,
          occupation: formData.occupation,
          // Keep existing image if not changed
          image: formData.image || user?.image,
        };
        setUser(updatedUser);

        // Update local state
        setFormData((prev) => ({
          ...prev,
          image: formData.image || user?.image,
        }));
        setImage(formData.image || user?.image);
        setIsEditing(false);
        setIsUpdating(false);

        // Invalidate React Query cache to refresh HeaderLeftUser and other components
        queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      } else {
        setError("Update profile failed.");
        toast.error("Update profile failed.");
        setIsUpdating(false);
      }
    } catch (error) {
      setError("Error updating profile.");
      toast.error("Error updating profile.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteImage = async () => {
    setError("");
    setSuccessMessage("");
    const token = retrieve(AUTH_TOKEN);
    try {
      setIsDeleting(true);
      const response = await fetch(`${API_BASE}/user/delete/image`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.status === true) {
        toast.success("Image deleted successfully!");

        // Update Firebase
        if (user?.id) {
          await updateUser(String(user.id), {
            profileImage: null,
            image: null,
          });
        }

        // Update zustand store and localStorage
        const updatedUser = {
          ...user,
          image: null,
          profileImage: null,
        };
        setUser(updatedUser);

        // Update local state
        setImage(null);
        setImagePreview(null);

        // Invalidate React Query cache to refresh HeaderLeftUser and other components
        queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      } else {
        setError("Failed to delete image.");
        toast.error("Failed to delete image.");
      }
    } catch (err) {
      setError("Error deleting image.");
      toast.error("Error deleting image");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        if (successMessage) setSuccessMessage("");
        if (error) setError(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  // Guard render if user still null (avoid crashes)
  if (!user) {
    return (
      <div className="tab-pane fade show active">
        <div className="profile-container">
          <h1 className="setting-header text-heading-h3 mb-3 mb-md-4">
            Profile
          </h1>
          <p className="text-body-b3 mb-0">Loading user...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-pane fade show active">
      <div className="profile-container">
        <h1 className="setting-header text-heading-h3 mb-3 mb-md-4">Profile</h1>
        <div className="setting-profile-details-container">
          <div className="d-flex gap-3 gap-xl-4 align-items-center">
            <img
              alt="Profile"
              className="setting-profile-image"
              src={imagePreview || image || "/images/profile-image.jpeg"}
            />
            <div className="setting-profile-details">
              {user && (
                <>
                  <NameDisplay
                    name={user.full_name}
                    textStyle={"text-body-b2"}
                    maxLength={20}
                  />
                  <span className="text-body-b3">{user.occupation}</span>
                </>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 gap-xl-4 flex-grow-1 justify-content-end justify-content-md-center profile-btn-container">
            <button
              className="btn-3 btn-1-primary text-body-b3"
              style={{
                maxWidth: "168px",
                whiteSpace: "nowrap",
                width: "100%",
                padding: "6px 20px",
              }}
              onClick={() =>
                document.getElementById("profileImageUpload").click()
              }
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Upload new photo"}
            </button>
            <input
              id="profileImageUpload"
              type="file"
              className="d-none"
              onChange={handleFileChange}
            />
            <button
              className="btn-3 btn-1-basic text-body-b3"
              style={{ maxWidth: "151px", width: "100%", padding: "6px 20px" }}
              onClick={handleDeleteImage}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
        <hr className="setting-profile-horizontal-line" />
        {!isEditing ? (
          <div
            id="profile-details-text"
            className="setting-profile-information-container"
          >
            <div className="row g-3 g-sm-4">
              <div className="col-md-6">
                <div className="setting-profile-information">
                  <span className="text-body-b3">Name</span>
                  {user && (
                    <NameDisplay
                      name={user.full_name}
                      textStyle={"text-body-b3"}
                      maxLength={20}
                    />
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="setting-profile-information">
                  <span className="text-body-b3">Email</span>
                  <h4 className="text-body-b3 mb-0">
                    {user?.email || "Not set"}
                  </h4>
                </div>
              </div>
              <div className="col-md-6">
                <div className="setting-profile-information">
                  <span className="text-body-b3">Mobile Number</span>
                  <h4 className="text-body-b3 mb-0">
                    {user?.phone_number || "Not set"}
                  </h4>
                </div>
              </div>
              <div className="col-md-6">
                <div className="setting-profile-information">
                  <span className="text-body-b3">Occupation</span>
                  <h4 className="text-body-b3 mb-0">
                    {user?.occupation || "Not set"}
                  </h4>
                </div>
              </div>
            </div>
            {successMessage && (
              <p className="text-success mt-2">{successMessage}</p>
            )}
            {error && (
              <p style={{ color: "red", marginTop: "16px" }}>{error}</p>
            )}
            <div className="mt-3 mt-md-0">
              <button
                className="btn-1 btn-1-primary text-body-b1"
                style={{ maxWidth: "240px", width: "100%" }}
                onClick={handleEditClick}
              >
                Edit
              </button>
            </div>
          </div>
        ) : (
          <div
            id="profile-details-form"
            className="setting-profile-information-container"
          >
            <form className="d-flex flex-column flex-grow-1 justify-content-between">
              <div className="row g-3 g-sm-4">
                <div className="col-md-6">
                  <div className="textfield-1">
                    <input
                      type="text"
                      name="full_name"
                      className="text-body-b2"
                      placeholder=""
                      value={formData.full_name}
                      onChange={handleInputChange}
                    />
                    <label className="text-body-b2">Name</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="textfield-1">
                    <input
                      type="email"
                      name="email"
                      className="text-body-b2"
                      placeholder=""
                      value={formData.email}
                      disabled
                    />
                    <label className="text-body-b2">Email</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="textfield-1">
                    <input
                      type="text"
                      name="phone_number"
                      className="text-body-b2"
                      placeholder=""
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      maxLength={10}
                    />
                    <label className="text-body-b2">Mobile number</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="textfield-1">
                    <input
                      type="text"
                      name="occupation"
                      className="text-body-b2"
                      placeholder=""
                      value={formData.occupation}
                      onChange={handleInputChange}
                    />
                    <label className="text-body-b2">Occupation</label>
                  </div>
                </div>
              </div>
              {successMessage && (
                <p className="text-success mt-2">{successMessage}</p>
              )}
              {error && (
                <p style={{ color: "red", marginTop: "16px" }}>{error}</p>
              )}
              <div className="d-flex gap-md-4 gap-3 mt-3 mt-md-0 flex-wrap profile-save-edit-btn">
                <button
                  className="btn-1 btn-1-primary text-body-b1"
                  style={{ maxWidth: "240px", width: "100%" }}
                  onClick={handleProfileUpdate}
                  disabled={isUpdating}
                >
                  {isUpdating ? "Saving..." : "Save"}
                </button>
                <button
                  className="btn-1 btn-1-basic text-body-b1"
                  style={{ maxWidth: "168px", width: "100%" }}
                  onClick={handleCancelClick}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
